import { REPORT_REASON_LABELS, type ReportReason } from '@sirocco/shared';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { events, messages, posts, users } from '../../api/endpoints';
import { SubScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { TextArea } from '../../components/ui/Field';
import { leaveFlash } from '../../utils/flash';
import { t } from '../../i18n';

/** Ordre des motifs : celui de la liste partagee, "Autre" en dernier. */
const REASONS = Object.keys(REPORT_REASON_LABELS) as ReportReason[];

/** Ce qu'on peut signaler, et ce que la phrase d'accroche en dit. */
const KINDS = {
  post: t.moderation.signaler.quoi.post,
  comment: t.moderation.signaler.quoi.comment,
  event: t.moderation.signaler.quoi.event,
  member: t.moderation.signaler.quoi.member,
  conversation: t.moderation.signaler.quoi.conversation,
} as const;

type ReportKind = keyof typeof KINDS;

/**
 * Signaler un contenu.
 *
 * Un seul ecran pour tout ce qui se signale — post, reponse, evenement,
 * membre, conversation — la ou chaque ecran portait sa propre liste de
 * motifs au fond d'une feuille d'actions. Le motif y etait a moitie
 * cache, et rien ne permettait de dire ce qui s'etait passe : c'est
 * pourtant ce detail-la qui fait la difference cote moderation.
 *
 * La cible voyage dans l'adresse plutot que dans l'etat de navigation :
 * un signalement recharge en cours de route ne doit pas perdre ce qu'il
 * signale.
 */
export function ReportScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const kind = (params.get('kind') ?? '') as ReportKind;
  const id = params.get('id') ?? '';

  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function send() {
    if (!reason) return;
    setPending(true);
    setFailure(null);
    try {
      const response = await dispatch(kind, id, params, reason, details);
      /*
        Le message s'affiche a l'arrivee, pas ici : on revient d'ou l'on
        vient, et c'est la-bas qu'un post signale doit avoir disparu.
      */
      leaveFlash(response.message);
      navigate(-1);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.moderation.signaler.impossible);
      setPending(false);
    }
  }

  // Une cible inconnue ne se signale pas : mieux vaut le dire que de
  // laisser remplir un formulaire qui ne partira jamais.
  if (!KINDS[kind] || !id) {
    return (
      <SubScreen title={t.moderation.signaler.titre}>
        <p className="pr-intro">{t.moderation.signaler.indisponible}</p>
      </SubScreen>
    );
  }

  return (
    <SubScreen
      title={t.moderation.signaler.titre}
      bottomBar={
        <div className="pr-help__send">
          <Button loading={pending} disabled={reason === null} onClick={() => void send()}>
            {t.moderation.signaler.envoyer}
          </Button>
        </div>
      }
    >
      <p className="pr-intro">
        {t.moderation.signaler.intro(KINDS[kind])}
      </p>

      <p className="pr-help__label">{t.moderation.signaler.motif}</p>
      <div className="pr-help__topics">
        {REASONS.map((value) => (
          <button
            key={value}
            className={`pr-help__topic${reason === value ? ' pr-help__topic--on' : ''}`}
            aria-pressed={reason === value}
            // Un second appui sur le meme motif le deselectionne.
            onClick={() => setReason(reason === value ? null : value)}
          >
            {t.moderation.raisons[value]}
          </button>
        ))}
      </div>

      <TextArea
        tone="spot"
        label={t.moderation.signaler.detail}
        className="pr-help__area"
        placeholder={t.moderation.signaler.detailExemple}
        rows={6}
        maxLength={1200}
        value={details}
        onChange={(changed) => setDetails(changed.target.value)}
      />

      {failure && <p className="field__error">{failure}</p>}
    </SubScreen>
  );
}

/**
 * Envoie le signalement a la bonne facade.
 *
 * Chaque type garde ses effets de bord : signaler un post le masque,
 * signaler un membre le bloque. Ces regles vivaient deja dans la couche
 * donnee, l'ecran ne fait que choisir la porte.
 */
function dispatch(
  kind: ReportKind,
  id: string,
  params: URLSearchParams,
  reason: ReportReason,
  details: string,
): Promise<{ message: string }> {
  const serverId = params.get('server') ?? '';

  switch (kind) {
    case 'post':
      return posts.report({ id, serverId }, reason, details);
    case 'comment':
      return posts.reportComment({ id, postId: params.get('post') ?? '' }, reason, details);
    case 'event':
      return events.report({ id, serverId }, reason, details);
    case 'member':
      return users.report(id, reason, details);
    case 'conversation':
      return messages.report(id, reason, details);
  }
}

/** L'adresse de l'ecran de signalement pour une cible donnee. */
export function reportPath(
  kind: ReportKind,
  id: string,
  extra?: { serverId?: string; postId?: string },
): string {
  const params = new URLSearchParams({ kind, id });
  if (extra?.serverId) params.set('server', extra.serverId);
  if (extra?.postId) params.set('post', extra.postId);
  return `/app/report?${params.toString()}`;
}
